/* -------------------------------------------------------------------------- */
/*                                Input styles                */
/* -------------------------------------------------------------------------- */

@import './bq-input.variables';

:host {
  @apply block is-full;
}

/* -------------------------------------------------------------------------- */
/*                            Label and Helper text                           */
/* -------------------------------------------------------------------------- */

.bq-input--label {
  @apply flex flex-grow items-center m-be-[--bq-input--label-margin-bottom];
  @apply text-[length:--bq-input--label-text-size] text-[color:--bq-input--label-text-color];
}

.bq-input--helper-text {
  @apply text-[length:--bq-input--helper-text-size] text-[color:--bq-input--helper-text-color] m-bs-[--bq-input--helper-margin-top];
}

.bq-input--helper-text.validation-error {
  @apply text-danger;
}

.bq-input--helper-text.validation-success {
  @apply text-success;
}

.bq-input--helper-text.validation-warning {
  @apply text-warning;
}

/* -------------------------------------------------------------------------- */
/*                             Input group control                            */
/* -------------------------------------------------------------------------- */

.bq-input--control {
  @apply flex items-center transition-[border-color,box-shadow] is-full;
  // Border
  @apply rounded-[--bq-input--border-radius] border-[length:--bq-input--border-width] border-[color:--bq-input--border-color];
  // Padding
  @apply pe-[--bq-input--padding-end] ps-[--bq-input--padding-start] p-b-[--bq-input--paddingY];
  // Text
  @apply text-[length:--bq-input--text-size] text-[color:--bq-input--text-color] placeholder:text-[color:--bq-input--text-placeholder-color];
  // Hover
  @apply [&:not(.disabled):not(:focus-within)]:hover:border-hover-brand;

  border-style: var(--bq-input--border-style);

  // Focus
  &:not(.disabled):focus-within {
    --bq-ring-width: 1px;
    --bq-ring-offset-width: 0;
    --bq-ring-color-focus: var(--bq-input--border-color-focus);

    @apply focus border-[color:--bq-input--border-color-focus];
  }

  // Enable clear button whenever the input group control is hovered or has focus
  &:not(.disabled):hover,
  &:not(.disabled):focus-within {
    .bq-input--control__clear {
      @apply inline-block;
    }
  }
}

.bq-input--control.disabled {
  @apply cursor-not-allowed opacity-60;
}

/* ------------------------------- Validation ------------------------------- */

.bq-input--control.validation-error {
  @apply border-danger [&:not(.disabled):not(:focus-within)]:hover:border-hover-danger;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.danger);

    @apply border-active-danger;
  }
}

.bq-input--control.validation-success {
  @apply border-success [&:not(.disabled):not(:focus-within)]:hover:border-hover-success;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.success);

    @apply border-active-success;
  }
}

.bq-input--control.validation-warning {
  @apply border-warning [&:not(.disabled):not(:focus-within)]:hover:border-hover-warning;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.warning);

    @apply border-active-warning;
  }
}

/* -------------------------------------------------------------------------- */
/*                              Native HTML Input                             */
/* -------------------------------------------------------------------------- */

.bq-input--control__input {
  @apply flex-auto cursor-inherit appearance-none bg-inherit font-inherit text-[length:inherit] text-inherit;
  @apply border-none min-bs-[--bq-input--icon-size] min-is-0 p-b-0 p-i-0 m-b-0 m-i-0 focus:outline-none focus-visible:outline-none;

  box-shadow: none;
  font-weight: inherit;
}

/* -------------------------------------------------------------------------- */
/*                                Clear button                                */
/* -------------------------------------------------------------------------- */

.bq-input--control__clear::part(button) {
  // Since the clear button is inside the input group control,
  // we need to reset the focus ring styles
  --bq-ring-width: initial;
  --bq-ring-offset-width: initial;
  --bq-ring-color-focus: initial;
}

/* -------------------------------------------------------------------------- */
/*                              Prefix and suffix                             */
/* -------------------------------------------------------------------------- */

.bq-input--control__prefix,
.bq-input--control__suffix {
  @apply pointer-events-none flex items-center text-[color:--bq-input--text-color];
}

.bq-input--control__prefix {
  @apply me-[--bq-input--gap];
}

.bq-input--control__suffix {
  @apply ms-[--bq-input--gap];
}

/* -------------------------------------------------------------------------- */
/*                         Slotted and internal icons                         */
/* -------------------------------------------------------------------------- */

bq-icon,
::slotted(bq-icon) {
  --bq-icon--size: var(--bq-input--icon-size) !important;
}

/* -------------------------------------------------------------------------- */
/*                          Hide webkit clear button                          */
/* -------------------------------------------------------------------------- */

/* Remove clear controls from search input */

.bq-input--control__input::-moz-search-cancel {
  @apply hidden appearance-none;
}

.bq-input--control__input::-webkit-search-decoration,
.bq-input--control__input::-webkit-search-cancel-button,
.bq-input--control__input::-webkit-search-results-button,
.bq-input--control__input::-webkit-search-results-decoration {
  @apply hidden appearance-none;
}

/* Remove native control extra padding from input date and datetime */

.bq-input--control__input::-webkit-datetime-edit-fields-wrapper,
.bq-input--control__input::-webkit-datetime-edit,
.bq-input--control__input::-webkit-datetime-edit-year-field,
.bq-input--control__input::-webkit-datetime-edit-month-field,
.bq-input--control__input::-webkit-datetime-edit-day-field,
.bq-input--control__input::-webkit-datetime-edit-hour-field,
.bq-input--control__input::-webkit-datetime-edit-minute-field,
.bq-input--control__input::-webkit-datetime-edit-second-field,
.bq-input--control__input::-webkit-datetime-edit-millisecond-field,
.bq-input--control__input::-webkit-datetime-edit-meridiem-field {
  @apply p-b-0 p-i-0;
}
